import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TermsContent from '../TermsContent';
import PrivacyContent from '../PrivacyContent';

const BRAND_BLUE = '#043663';

const SectionTitle = styled.p`
  font-size: 0.8125rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.75rem 0;
`;

const CheckboxGroup = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 0 0 1rem 0;
`;

const Checkbox = styled.input`
  margin-top: 0.2rem;
  flex-shrink: 0;
`;

const CheckboxLabel = styled.label`
  font-size: 0.8125rem;
  line-height: 1.45;
  cursor: pointer;
  color: #374151;
`;

const TermsLink = styled.button.attrs({ type: 'button' })`
  background: none;
  border: none;
  color: ${BRAND_BLUE};
  text-decoration: none;
  cursor: pointer;
  font-size: inherit;
  font-family: inherit;
  font-weight: 600;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const TermsModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 20px;
`;

const TermsModalContent = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 800px;
  max-height: 90vh;
  width: 100%;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
`;

const TermsModalHeader = styled.div`
  background: linear-gradient(135deg, ${BRAND_BLUE} 0%, #032a4a 100%);
  color: white;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const TermsModalTitle = styled.h2`
  margin: 0;
  font-size: 1.35rem;
  font-weight: 600;
`;

const TermsCloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: background-color 0.2s;
  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`;

const TermsModalBody = styled.div`
  padding: 2rem;
  overflow-y: auto;
  flex: 1;
  line-height: 1.6;
  color: #111827;
`;

const LegalModalLink = styled(Link)`
  display: inline-block;
  margin-bottom: 1rem;
  color: ${BRAND_BLUE};
  font-weight: 600;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

function TermsModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <TermsModalOverlay onClick={onClose}>
      <TermsModalContent onClick={(e) => e.stopPropagation()}>
        <TermsModalHeader>
          <TermsModalTitle>{t('termsWebsite.page.headerTitle')}</TermsModalTitle>
          <TermsCloseButton type="button" onClick={onClose} aria-label={t('register.modalCloseAria')}>
            ×
          </TermsCloseButton>
        </TermsModalHeader>
        <TermsModalBody>
          <LegalModalLink to="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
            {t('register.viewLegalFullPage')}
          </LegalModalLink>
          <TermsContent />
        </TermsModalBody>
      </TermsModalContent>
    </TermsModalOverlay>
  );
}

function PrivacyModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return (
    <TermsModalOverlay onClick={onClose}>
      <TermsModalContent onClick={(e) => e.stopPropagation()}>
        <TermsModalHeader>
          <TermsModalTitle>{t('privacyWebsite.page.headerTitle')}</TermsModalTitle>
          <TermsCloseButton type="button" onClick={onClose} aria-label={t('register.modalCloseAria')}>
            ×
          </TermsCloseButton>
        </TermsModalHeader>
        <TermsModalBody>
          <LegalModalLink to="/privacy-policy" target="_blank" rel="noopener noreferrer">
            {t('register.viewLegalFullPage')}
          </LegalModalLink>
          <PrivacyContent />
        </TermsModalBody>
      </TermsModalContent>
    </TermsModalOverlay>
  );
}

/**
 * Same legal consents as team registration (RegisterPage): T&C + Privacy (one checkbox), League rules (required), partner opt-in (optional).
 */
export default function PlayerRegistrationConsentSection({
  agreeTerms,
  onAgreeTermsChange,
  agreeRules,
  onAgreeRulesChange,
  partnerCommunications,
  onPartnerCommunicationsChange,
}) {
  const { t } = useTranslation();
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);

  return (
    <>
      <SectionTitle>{t('register.consentSectionTitle')}</SectionTitle>

      <CheckboxGroup>
        <Checkbox
          id="player-signup-agree-terms"
          type="checkbox"
          checked={agreeTerms}
          onChange={(e) => onAgreeTermsChange(e.target.checked)}
        />
        <CheckboxLabel htmlFor="player-signup-agree-terms">
          {t('register.agreeTerms')}{' '}
          <TermsLink type="button" onClick={() => setShowTermsModal(true)}>
            {t('register.termsAndConditions')}
          </TermsLink>{' '}
          {t('register.and')}{' '}
          <TermsLink type="button" onClick={() => setShowPrivacyModal(true)}>
            {t('register.privacyPolicy')}
          </TermsLink>{' '}
          *
        </CheckboxLabel>
      </CheckboxGroup>

      <CheckboxGroup>
        <Checkbox
          id="player-signup-agree-rules"
          type="checkbox"
          checked={agreeRules}
          onChange={(e) => onAgreeRulesChange(e.target.checked)}
        />
        <CheckboxLabel htmlFor="player-signup-agree-rules">{t('register.agreeRules')} *</CheckboxLabel>
      </CheckboxGroup>

      <CheckboxGroup>
        <Checkbox
          id="player-signup-partner-comms"
          type="checkbox"
          checked={partnerCommunications}
          onChange={(e) => onPartnerCommunicationsChange(e.target.checked)}
        />
        <CheckboxLabel htmlFor="player-signup-partner-comms">
          {t('register.partnerCommunicationsOptIn')}
        </CheckboxLabel>
      </CheckboxGroup>

      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
      <PrivacyModal isOpen={showPrivacyModal} onClose={() => setShowPrivacyModal(false)} />
    </>
  );
}
